/**
 * @fileoverview ARITA master-library browser (firm side).
 *
 * Shown as the "ARITA Library" tab inside the firm's Docs settings when the
 * firm has been granted access. Lists the master precedent catalogue and lets
 * an admin add precedents to their own library — one by one, a multi-select
 * batch, or all at once.
 *
 * Kept deliberately read-only and self-contained: the heavy editable
 * TemplateCard is firm-org-scoped and would mis-target the master org for a
 * firm user, so this surface only displays + adds. Once added, precedents
 * appear in "My Library" where the full editor applies.
 */

import { useCallback, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexAction } from "@convex-dev/react-query";
import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";
import { Spinner, Badge, Button, Checkbox, Input } from "@/ui/primitives";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import { toast } from "sonner";
import {
  Library,
  Search,
  X as XIcon,
  Plus,
  Check,
  FileText,
  Loader2,
} from "lucide-react";

const CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] overflow-hidden";

type MasterTemplate = {
  _id: Id<"precedentTemplates">;
  name: string;
  displayName?: string;
  description?: string;
  templateType?: "form" | "letter" | "insurance";
  categoryName?: string;
  alreadyAdded: boolean;
};

function templateTypeLabel(t?: string): string {
  if (t === "form") return "Form";
  if (t === "letter") return "Letter";
  if (t === "insurance") return "Web Portal";
  return "Template";
}

export function AritaLibraryTab({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const { data: templates, isLoading } = useQuery({
    ...convexQuery(api.settings.precedents.master.listMasterTemplatesForFirm, {
      orgId,
    }),
    enabled: !!orgId,
  });

  const { mutateAsync: addToFirm, isPending: adding } = useMutation({
    mutationFn: useConvexAction(
      api.settings.precedents.masterActions.addToFirm,
    ),
  });

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<Id<"precedentTemplates">>>(
    () => new Set(),
  );

  const rows = useMemo(
    () => (templates ?? []) as MasterTemplate[],
    [templates],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (t) =>
        (t.displayName ?? t.name ?? "").toLowerCase().includes(q) ||
        (t.description ?? "").toLowerCase().includes(q) ||
        (t.categoryName ?? "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  // Only un-added templates are selectable / addable.
  const addable = useMemo(
    () => filtered.filter((t) => !t.alreadyAdded),
    [filtered],
  );

  const toggleSelect = useCallback((id: Id<"precedentTemplates">) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const runAdd = useCallback(
    async (ids: Array<Id<"precedentTemplates">>) => {
      if (ids.length === 0) return;
      try {
        const result = await addToFirm({ orgId, masterTemplateIds: ids });
        const n = result?.newTemplateIds?.length ?? 0;
        const skipped = result?.skipped ?? 0;
        if (n > 0) {
          toast.success(
            `Added ${n} precedent${n !== 1 ? "s" : ""} to your library`,
          );
        }
        if (skipped > 0 && n === 0) {
          toast.info("Those precedents are already in your library");
        }
        setSelected(new Set());
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to add precedents"));
      }
    },
    [addToFirm, orgId],
  );

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-20">
        <Spinner className="size-6" />
      </div>
    );
  }

  const allSelected =
    addable.length > 0 && addable.every((t) => selected.has(t._id));

  return (
    <div className={CARD_CLASS}>
      <div className="flex items-start justify-between px-5 py-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Library className="size-4.5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-primary">
              ARITA Master Library
            </h3>
            <p className="mt-1 text-sm text-primary/60">
              Add shared ARITA precedents to your firm library. Edits you make
              after adding stay in your firm only.
            </p>
          </div>
        </div>
        <Badge variant="secondary" className="shrink-0">
          {rows.length} available
        </Badge>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 px-5 pb-4">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-primary/40" />
          <Input
            placeholder="Search ARITA precedents…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-9 pr-9 text-sm"
          />
          {search && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-primary/40 hover:bg-secondary/60 hover:text-primary/70"
            >
              <XIcon className="size-3" />
            </button>
          )}
        </div>

        {canEdit && addable.length > 0 && (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 gap-1.5 text-xs"
              onClick={() =>
                setSelected(
                  allSelected ? new Set() : new Set(addable.map((t) => t._id)),
                )
              }
            >
              <Check className="size-3.5" />
              {allSelected ? "Deselect all" : "Select all"}
            </Button>
            {selected.size > 0 && (
              <Button
                type="button"
                size="sm"
                className="h-9 gap-1.5 text-xs"
                disabled={adding}
                onClick={() => runAdd(Array.from(selected))}
              >
                {adding ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Plus className="size-3.5" />
                )}
                Add selected ({selected.size})
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-9 gap-1.5 text-xs"
              disabled={adding}
              onClick={() => runAdd(addable.map((t) => t._id))}
            >
              <Plus className="size-3.5" />
              Add all
            </Button>
          </>
        )}
      </div>

      {/* Grid */}
      <div className="border-t border-border/60 p-5">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
            <Library className="size-8 text-primary/30" />
            <p className="text-sm text-primary/60">
              {rows.length === 0
                ? "No ARITA precedents are available yet."
                : "No precedents match your search."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((t) => {
              const isSelected = selected.has(t._id);
              const name = t.displayName ?? t.name;
              return (
                <div
                  key={t._id}
                  className={cn(
                    "group relative flex flex-col rounded-xl border border-border/50 bg-card p-4 transition",
                    isSelected && "ring-2 ring-primary ring-offset-1",
                  )}
                >
                  {canEdit && !t.alreadyAdded && (
                    <div className="absolute right-2 top-2 z-10">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(t._id)}
                        className="size-4 border-primary/30 bg-background"
                      />
                    </div>
                  )}

                  <div className="flex items-start gap-2.5">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-secondary/60 text-primary/70">
                      <FileText className="size-4" />
                    </div>
                    <div className="min-w-0 flex-1 pr-5">
                      <p className="truncate text-sm font-medium text-primary">
                        {name}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        <Badge
                          variant="secondary"
                          className="bg-blue-100 text-[10px] text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
                        >
                          ARITA
                        </Badge>
                        <Badge variant="secondary" className="text-[10px]">
                          {templateTypeLabel(t.templateType)}
                        </Badge>
                        {t.categoryName && (
                          <Badge variant="outline" className="text-[10px]">
                            {t.categoryName}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {t.description && (
                    <p className="mt-2 line-clamp-2 text-xs text-primary/55">
                      {t.description}
                    </p>
                  )}

                  <div className="mt-3 flex items-center justify-end">
                    {t.alreadyAdded ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                        <Check className="size-3.5" />
                        Added
                      </span>
                    ) : canEdit ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 gap-1.5 text-xs"
                        disabled={adding}
                        onClick={() => runAdd([t._id])}
                      >
                        <Plus className="size-3.5" />
                        Add
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
